import { type ExperimentsFlags as AbExperimentsFlags } from '@adv-frontend/yastore-modules/lib';
import { type DomainOperationStatus } from '#shared/api/schema/openApi/links/DomainOperationStatus';
import { type OpenApiSchemaProgramType } from '../../api/schema';
import { OZON_SERVICE_NAME } from '../brand';

export type FlagValue = 'exp' | 'ctl';

type OverrideOnlyFlags = {
    enable_merchant_delivery_multiple_slots?: FlagValue;
    force_enable_snowflakes?: FlagValue;
    alty_queue?: string;
    enable_quick_filter_auto?: FlagValue;
    override_domain_status?: DomainOperationStatus;
    override_program?: OpenApiSchemaProgramType;
    override_spent_last_month?: string;
    override_spent_current_month?: string;
    override_goal_reached_last_month?: string;
    override_goal_reached_current_month?: string;
    goal_value?: string;
    yandex_direct_balance?: string;
    need_yandex_direct_balance_alert?: string;
    override_program_version?: 'OLD' | 'NEW';
    alert_period?: 'NONE' | 'CURRENT' | 'PREVIOUS';
    co_invest_percent?: string;
    alty_file_upload?: FlagValue;
    enable_ozon_groups?: FlagValue;
    enable_other_orders_grid_dynamic_filters?: FlagValue;
    enable_order_refunds_automation?: FlagValue;
    enable_cloud_kassir_test_mode?: FlagValue;
};

export type ExperimentFlags = AbExperimentsFlags & OverrideOnlyFlags;

export type FlagName = keyof AbExperimentsFlags | keyof ExperimentFlags;

export const experimentFlags: ExperimentFlags = {
    enable_checkout_editor: undefined,
    enable_merchant_delivery_multiple_slots: undefined,
    force_enable_snowflakes: undefined,
    alty_queue: undefined,
    enable_quick_filter_auto: undefined,
    override_domain_status: undefined,
    override_program: undefined,
    override_spent_last_month: undefined,
    override_spent_current_month: undefined,
    override_goal_reached_last_month: undefined,
    override_goal_reached_current_month: undefined,
    goal_value: undefined,
    yandex_direct_balance: undefined,
    need_yandex_direct_balance_alert: undefined,
    override_program_version: undefined,
    alert_period: undefined,
    co_invest_percent: undefined,
    enable_order_settings_full: undefined,
    alty_file_upload: undefined,
    enable_ozon_groups: undefined,
    enable_ab_healthy_check: undefined,
    enable_direct_billing: undefined,
    enable_other_orders_grid_dynamic_filters: undefined,
    enable_mindbox_integration: undefined,
    enable_checkout_order_edit: undefined,
    enable_checkout_binding_auth: undefined,
    enable_discount_ladder: undefined,
    enable_cart_preview: undefined,
    enable_order_refunds_automation: undefined,
    enable_cloud_kassir_test_mode: undefined,
    enable_ozon_pay: undefined,
    enable_facet_collections: undefined,
    enable_additional_products_checkout_info: undefined,
    enable_email_constructor: undefined,
    enable_ycp_publish_btn_and_hide_alert: undefined,
};

export const flagDescriptions: Partial<Record<keyof ExperimentFlags, string>> = {
    enable_checkout_editor: 'Включение страницы настройки оформления чекаута во всех кабинетах',
    enable_merchant_delivery_multiple_slots:
        'Новый интерфейс графика собственной доставки с несколькими слотами в день',
    force_enable_snowflakes: 'Принудительное включение снежинок',
    alty_queue: 'Очередь для AI-ассистента (строковое значение)',
    enable_quick_filter_auto: 'Автоматический выбор быстрых фильтров коллекции',
    override_domain_status: 'Принудительное переопределение статуса домена',
    override_program: 'Принудительное переопределение программы',
    override_spent_last_month: 'Принудительное переопределение доли откруток за прошлый месяц (проценты)',
    override_spent_current_month:
        'Принудительное переопределение доли откруток за текущий месяц или отображаемое окно новой программы (проценты)',
    override_goal_reached_last_month: 'Принудительное переопределение достижения цели за прошлый месяц (1/0)',
    override_goal_reached_current_month:
        'Принудительное переопределение достижения цели за текущий месяц или отображаемое окно новой программы (1/0)',
    goal_value: 'Принудительное переопределение значения цели по откруткам для старой и новой программы',
    yandex_direct_balance: 'Принудительное переопределение баланса Директа',
    need_yandex_direct_balance_alert: 'Принудительное переопределение показа алёрта о балансе Директа',
    override_program_version: 'Принудительное переопределение версии программы софинансирования',
    alert_period: 'Принудительное переопределение периода алёрта новой программы',
    co_invest_percent: 'Принудительное переопределение процента соинвестирования новой программы',
    enable_order_settings_full: 'Включение всех настроек по заказам',
    alty_file_upload: 'Включение загрузки файлов в AI-ассистенте',
    enable_ozon_groups: `Включение групп характеристик (${OZON_SERVICE_NAME})`,
    enable_ab_healthy_check: 'Включение проверки здоровья AB (индикатор в контроллере флагов)',
    enable_direct_billing: 'Включение откруток, алёртов и баланса Директа в виджете на Главной',
    enable_other_orders_grid_dynamic_filters:
        'Включение еще нереализованных на беке динамических фильтров в гриде заказов',
    enable_checkout_order_edit: 'Редактирование заказа в чекауте',
    enable_checkout_binding_auth: 'Авторизация чекаута через binding_token и sharing API',
    enable_discount_ladder: 'Включение лестницы скидок',
    enable_cart_preview: 'Показ страницы превью корзины в конструкторе',
    enable_mindbox_integration: 'Интеграция Mindbox',
    enable_order_refunds_automation: 'Включения настройки отключения автоматики чеков',
    enable_cloud_kassir_test_mode:
        'Тестовый режим подключения кассы CloudKassir: поля «Заводской номер» и «Номер ФН» необязательны (тестовая касса работает по Public ID). Бэкенд принимает пустые номера только вне prod',
    enable_ozon_pay: 'Включение возможности подключения Ozon Pay как эквайринга',
    enable_facet_collections: 'Переход к автоколлекциям по значениям характеристик',
    enable_additional_products_checkout_info:
        'Показ инфоблока про апсейл на чекауте в форме товара и секции дополнительных товаров в настройках заказов',
    enable_email_constructor: 'Включение конструктора email-писем',
    enable_ycp_publish_btn_and_hide_alert:
        'Включить у внешних чекаутов кнопку "Опубликовать" и убрать алерт о нестабильной работе',
};

export const flagKey = 'flags';

/**
 * Массив флагов, которые принимают строковые значения
 */
export const stringValueFlags: Array<keyof ExperimentFlags> = [
    'alty_queue',
    'override_domain_status',
    'override_program',
    'override_spent_last_month',
    'override_spent_current_month',
    'override_goal_reached_last_month',
    'override_goal_reached_current_month',
    'goal_value',
    'yandex_direct_balance',
    'need_yandex_direct_balance_alert',
    'override_program_version',
    'alert_period',
    'co_invest_percent',
];

export const enumValueFlags: Partial<Record<keyof ExperimentFlags, string[]>> = {
    override_domain_status: [
        'IN_PROGRESS',
        'CREATING_A_RECORDS',
        'WAIT_CNAME',
        'WAIT_IP_RECORDS',
        'WAIT_NS',
        'SUCCESS',
        'FAILED',
        'VALIDATION_IN_PROGRESS',
    ],
    override_program: ['NONE', 'BASIC', 'MEDIUM', 'OPTIMAL', 'PREMIUM'],
    override_program_version: ['OLD', 'NEW'],
    alert_period: ['NONE', 'CURRENT', 'PREVIOUS'],
};
